import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { sendDesktopNotification } from './ReminderSystem';

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

const dingAudio = new Audio('/ding.mp3');
function playDing() {
  dingAudio.currentTime = 0;
  dingAudio.play().catch(() => {});
}

const NAVIGABLE_TYPES = new Set(['leave_request', 'task', 'subtask']);

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const dropRef = useRef(null);
  const knownIds = useRef(null);

  async function fetchNotifications() {
    try {
      const res = await api.get('/notifications');
      const incoming = res.data;
      // Fire desktop notification + sound for genuinely new unread items
      if (knownIds.current !== null) {
        const newItems = (incoming.notifications || []).filter(
          n => !n.is_read && !knownIds.current.has(n.id)
        );
        if (newItems.length > 0) playDing();
        for (const n of newItems) {
          sendDesktopNotification(n.title, n.body || '');
        }
      }
      knownIds.current = new Set((incoming.notifications || []).map(n => n.id));
      setData(incoming);
    } catch { /* silent */ }
  }

  // Poll every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, 30000);
    return () => clearInterval(t);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function markRead(id) {
    await api.patch(`/notifications/${id}/read`);
    setData(d => ({
      ...d,
      unread: Math.max(0, d.unread - (d.notifications.find(n => n.id === id && !n.is_read) ? 1 : 0)),
      notifications: d.notifications.map(n => n.id === id ? { ...n, is_read: true } : n),
    }));
  }

  async function markAllRead() {
    await api.patch('/notifications/read-all/mark');
    setData(d => ({ unread: 0, notifications: d.notifications.map(n => ({ ...n, is_read: true })) }));
  }

  async function handleNotificationClick(n) {
    if (!n.is_read) markRead(n.id);
    if (n.entity_type === 'leave_request') {
      setOpen(false);
      navigate('/hr/leaves');
    } else if ((n.entity_type === 'task' || n.entity_type === 'subtask') && n.entity_id) {
      setOpen(false);
      try {
        const { data: task } = await api.get(`/tasks/${n.entity_id}`);
        if (task?.project_id) navigate(`/projects/${task.project_id}?task=${n.entity_id}`);
      } catch { /* task may have been deleted since — nowhere to go */ }
    }
  }

  function handleOpen() {
    setOpen(o => !o);
    if (!open) fetchNotifications(); // refresh on open
  }

  return (
    <div ref={dropRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        style={{
          position: 'relative', background: 'none', border: 'none',
          cursor: 'pointer', padding: '6px 8px', borderRadius: 8,
          color: 'var(--text2)', display: 'flex', alignItems: 'center',
        }}
        title="Notifications"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {data.unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            background: '#FF0033', color: '#fff',
            fontSize: 9, fontWeight: 700, borderRadius: 99,
            minWidth: 16, height: 16, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', lineHeight: 1,
          }}>
            {data.unread > 9 ? '9+' : data.unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)',
          width: 340, background: 'var(--surface)',
          border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          zIndex: 1000, overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', borderBottom: '1px solid var(--border)',
          }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text1)' }}>
              Notifications {data.unread > 0 && (
                <span style={{
                  background: '#FF0033', color: '#fff', borderRadius: 99,
                  fontSize: 10, fontWeight: 700, padding: '1px 6px', marginLeft: 6,
                }}>{data.unread}</span>
              )}
            </span>
            {data.unread > 0 && (
              <button onClick={markAllRead} style={{
                fontSize: 11, color: 'var(--accent)', background: 'none',
                border: 'none', cursor: 'pointer', fontWeight: 600,
              }}>
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {data.notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                No notifications yet
              </div>
            ) : data.notifications.map(n => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                  background: n.is_read ? 'transparent' : 'rgba(7,62,129,0.04)',
                  cursor: (!n.is_read || NAVIGABLE_TYPES.has(n.entity_type)) ? 'pointer' : 'default',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}
              >
                {/* Dot indicator */}
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 5,
                  background: n.is_read ? 'transparent' : '#073E81',
                }}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: n.is_read ? 400 : 600,
                    color: 'var(--text1)', marginBottom: 2,
                  }}>
                    {n.title}
                  </div>
                  {n.body && (
                    <div style={{
                      fontSize: 11, color: 'var(--text2)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {n.body}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                    {timeAgo(n.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
